/**
 * @file src/api/preview.js
 */
import api from './client';
import MoksyLogger from '../utils/logger';
import { readGuestToken } from './guestToken.js';

class PreviewAPI {
  /**
   * 🔍 FETCH PREVIEW
   * Logic: Retrieves the technical document for display.
   */
  static async getTechnicalDoc(projectId, options = {}) {
    try {
      const isGuestFlow = Boolean(options?.guest);
      const path = isGuestFlow
        ? `/guest/preview/${projectId}`
        : `/preview/${projectId}`;
      const guestToken = isGuestFlow ? readGuestToken() : null;
      const headers = guestToken ? { Authorization: `Bearer ${guestToken}` } : undefined;
      const response = await api.get(path, { headers });
      return response.data.data;
    } catch (error) {
      const isGuestFlow = Boolean(options?.guest);
      const status = error?.response?.status;
      if (!isGuestFlow && status === 404 && projectId) {
        try {
          await PreviewAPI.generate({ projectId });
          const retry = await api.get(`/preview/${projectId}`);
          return retry.data.data;
        } catch (retryError) {
          error = retryError;
        }
      }
      if (error?.response?.status !== 503 && import.meta.env.DEV) {
        MoksyLogger.error('PREVIEW.FETCH_FAILED', error, { projectId });
      }
      if (!error?.response?.data?.code) {
        error.response = error.response || {};
        error.response.data = {
          ...(error.response.data || {}),
          code: 'PREVIEW.FETCH_FAILED'
        };
      }
      throw error;
    }
  }

  static async getPublicTechnicalDoc(projectId, options = {}) {
    const params = {};
    if (options?.t) params.t = options.t;
    const response = await api.get(`/preview/public/${projectId}`, { params });
    return response.data.data;
  }

  static async generate(payload = {}) {
    const response = await api.post('/preview/generate', payload);
    return response.data.data;
  }

  static async sessions(params = {}) {
    const response = await api.get('/preview/sessions', { params });
    return response.data.data;
  }

  static async session(sessionId) {
    const response = await api.get(`/preview/sessions/${sessionId}`);
    return response.data.data;
  }

  static async refresh(sessionId, payload = {}) {
    const response = await api.post(`/preview/sessions/${sessionId}/refresh`, payload);
    return response.data.data;
  }

  static async destroy(sessionId) {
    const response = await api.delete(`/preview/sessions/${sessionId}`);
    return response.data.data;
  }

  static async verifyAccess(payload = {}) {
    const response = await api.post('/preview/access/verify', payload);
    return response.data.data;
  }

  static async metadata(slug) {
    const response = await api.get(`/preview/metadata/${slug}`);
    return response.data.data;
  }

  static async patchVisibility(sessionId, payload = {}) {
    const response = await api.patch(`/preview/${sessionId}/visibility`, payload);
    return response.data.data;
  }

  static async setPassword(sessionId, payload = {}) {
    const response = await api.post(`/preview/${sessionId}/password`, payload);
    return response.data.data;
  }

  static async shareLink(sessionId, payload = {}) {
    const response = await api.post(`/preview/${sessionId}/share-link`, payload);
    return response.data.data;
  }

  // Revoke the share link. Every link already sent stops working. Making the
  // preview private does not achieve this — a valid token grants access
  // regardless of visibility, which is the point of a token.
  static async revokeShareLink(sessionId) {
    const res = await api.delete(`/preview/${sessionId}/share-link`);
    return res.data?.data || res.data;
  }

  static async patchExpiry(sessionId, payload = {}) {
    const response = await api.patch(`/preview/${sessionId}/expiry`, payload);
    return response.data.data;
  }

  static async render(sessionId, params = {}) {
    const response = await api.get(`/preview/${sessionId}/render`, { params });
    return response.data.data;
  }

  static async patchSection(sessionId, sectionId, payload = {}) {
    const response = await api.patch(`/preview/${sessionId}/sections/${sectionId}`, payload);
    return response.data.data;
  }

  static async patchReportContent(sessionId, payload = {}) {
    const response = await api.patch(`/preview/${sessionId}/report-content`, payload);
    return response.data.data;
  }

  static async reviewAmendment(amendmentId, payload = {}) {
    const response = await api.patch(`/preview/amendments/${amendmentId}/review`, payload);
    return response.data.data;
  }

  static async promoteAmendmentToLearning(amendmentId, payload = {}) {
    const response = await api.post(`/preview/amendments/${amendmentId}/promote-learning`, payload);
    return response.data.data;
  }

  static async comments(sessionId) {
    const response = await api.get(`/preview/${sessionId}/comments`);
    return response.data.data;
  }

  static async addComment(sessionId, payload = {}) {
    const response = await api.post(`/preview/${sessionId}/comments`, payload);
    return response.data.data;
  }

  static async patchComment(commentId, payload = {}) {
    const response = await api.patch(`/preview/comments/${commentId}`, payload);
    return response.data.data;
  }

  static async deleteComment(commentId) {
    const response = await api.delete(`/preview/comments/${commentId}`);
    return response.data.data;
  }

  // Reviewer assignment. Holding the reviewer role grants nothing on its own —
  // the owner assigns a named person to a named preview.
  static async reviewers(sessionId) {
    const response = await api.get(`/preview/${sessionId}/reviewers`);
    return response.data.data;
  }

  static async assignReviewer(sessionId, payload = {}) {
    const response = await api.post(`/preview/${sessionId}/reviewers`, payload);
    return response.data.data;
  }

  static async removeReviewer(sessionId, userId) {
    const response = await api.delete(`/preview/${sessionId}/reviewers/${userId}`);
    return response.data.data;
  }

  static async patchBranding(sessionId, payload = {}) {
    const response = await api.patch(`/preview/${sessionId}/branding`, payload);
    return response.data.data;
  }

  static async putCustomDomain(sessionId, payload = {}) {
    const response = await api.put(`/preview/${sessionId}/custom-domain`, payload);
    return response.data.data;
  }

  static async adminAll(params = {}) {
    const response = await api.get('/admin/preview/all', { params });
    return response.data.data;
  }

  static async adminFlagged(params = {}) {
    const response = await api.get('/admin/preview/flagged', { params });
    return response.data.data;
  }

  static async adminTakeDown(sessionId, payload = {}) {
    const response = await api.post(`/admin/preview/${sessionId}/take-down`, payload);
    return response.data.data;
  }

  static async adminStats() {
    const response = await api.get('/admin/preview/stats');
    return response.data.data;
  }

  static async internalValidateSchema(payload = {}) {
    const response = await api.post('/internal/preview/validate-schema', payload);
    return response.data.data;
  }

  static async internalHealth() {
    const response = await api.get('/internal/preview/health');
    return response.data.data;
  }

  static async internalSyncAssets(payload = {}) {
    const response = await api.post('/internal/preview/sync-assets', payload);
    return response.data.data;
  }

  static async sysConfig() {
    const response = await api.get('/sys/preview/config');
    return response.data.data;
  }

  static async sysConfigMaxSessions(payload = {}) {
    const response = await api.patch('/sys/preview/config/max-sessions', payload);
    return response.data.data;
  }

  static async sysConfigComments(payload = {}) {
    const response = await api.patch('/sys/preview/config/comments', payload);
    return response.data.data;
  }

  static async sysCleanup(payload = {}) {
    const response = await api.post('/sys/preview/cleanup', payload);
    return response.data.data;
  }

}

export { PreviewAPI as preview };
